import { isMobilePhoneUserAgent } from '@/utils/user-agent/isMobilePhoneUserAgent';
import { isTabletUserAgent, isTouchMacUserAgent } from '@/utils/user-agent/isTabletUserAgent';

export type DevicePlatform = 'desktop' | 'mobile' | 'tablet';

export interface DevicePlatformResult {
    platform: DevicePlatform;
    isMobile: boolean;
    isTablet: boolean;
    isMobileOrTablet: boolean;
    isDesktop: boolean;
    /** Mobile phone in mobile or desktop-site UA mode (client input signals when UA is desktop). */
    isMobileAnyMode: boolean;
}

/**
 * the empty band between the two device classes: the widest phone portrait width (~430px)
 * and the narrowest tablet portrait width (the VDS `tablet-portrait-min` breakpoint).
 * The value that keeps a margin on both sides.
 */
const MAX_PHONE_SCREEN_WIDTH = 500;

export function hasPhoneLikeInputSignals(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    // userAgentData.mobile is the only signal that specifically means "phone" (not tablet).
    // WebKit does not implement it, so iOS always falls through to the checks below.
    if (typeof navigator !== 'undefined' && navigator.userAgentData?.mobile === true) {
        return true;
    }

    const hasCoarsePrimaryInput = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    // the match above covers all touch devices — phones + tablets/touch laptops — so gate on size to
    // exclude tablets. `screen` rather than the viewport: it is unaffected by desktop-site layout
    // and by iPad multitasking, where a Slide Over window is narrower than a phone.
    const shortestScreenSide = Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0);
    const isPhoneSizedScreen =
        shortestScreenSide > 0 && shortestScreenSide <= MAX_PHONE_SCREEN_WIDTH;

    return hasCoarsePrimaryInput && isPhoneSizedScreen;
}

export function getDevicePlatform(): DevicePlatformResult {
    const raw = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator.maxTouchPoints ?? 0) : 0;

    let platform: DevicePlatform;
    if (isMobilePhoneUserAgent(raw)) {
        platform = 'mobile';
    } else if (isTabletUserAgent(raw, maxTouchPoints)) {
        platform = 'tablet';
    } else {
        platform = 'desktop';
    }

    const isMobile = platform === 'mobile';
    const isTablet = platform === 'tablet';
    const isDesktop = platform === 'desktop';
    // iPhone in desktop mode is indistinguishable by UA from an iPad on its default UA,
    // the tablet branch above claims both; checking input signals for that case
    const isDesktopModeMobile =
        (isDesktop || isTouchMacUserAgent(raw, maxTouchPoints)) && hasPhoneLikeInputSignals();
    const isMobileAnyMode = isMobile || isDesktopModeMobile;

    return {
        platform,
        isMobile,
        isMobileAnyMode,
        isTablet,
        isMobileOrTablet: isMobile || isTablet,
        isDesktop,
    };
}
